import React, { useState, useRef } from 'react';

export class SelectionBox {
  constructor() {
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.isSelecting = false;
  }

  // Get the normalized rectangle coordinates (handles negative dimensions)
  getRect() {
    return {
      x: Math.min(this.startX, this.currentX),
      y: Math.min(this.startY, this.currentY),
      width: Math.abs(this.currentX - this.startX),
      height: Math.abs(this.currentY - this.startY)
    };
  }
}

export function useSelectionManager(onSelectionChange) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionBox, setSelectionBox] = useState(null);
  const selectionRef = useRef(new SelectionBox());
  const isDraggingRef = useRef(false);

  const startSelection = (e, svgRef) => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    selectionRef.current.isSelecting = true;
    selectionRef.current.startX = x;
    selectionRef.current.startY = y;
    selectionRef.current.currentX = x;
    selectionRef.current.currentY = y;

    setSelectionBox(selectionRef.current.getRect());
  };

  const updateSelection = (e, svgRef) => {
    if (!selectionRef.current.isSelecting || !svgRef.current) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    selectionRef.current.currentX = e.clientX - rect.left;
    selectionRef.current.currentY = e.clientY - rect.top;

    setSelectionBox(selectionRef.current.getRect());
  };

  const endSelection = (furniture) => {
    if (!selectionRef.current.isSelecting) return;

    const selectedItems = new Set();
    const selRect = selectionRef.current.getRect();

    // Check each furniture item for intersection with selection box
    furniture.forEach(item => {
      if (intersectsWithSelection(item, selRect)) {
        selectedItems.add(item.id);
      }
    });

    setSelectedIds(selectedItems);
    selectionRef.current.isSelecting = false;
    setSelectionBox(null);

    if (onSelectionChange) {
      onSelectionChange(selectedItems);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    if (onSelectionChange) {
      onSelectionChange(new Set());
    }
  };

  const addToSelection = (id) => {
    const newSelection = new Set(selectedIds);
    newSelection.add(id);
    setSelectedIds(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const removeFromSelection = (id) => {
    const newSelection = new Set(selectedIds);
    newSelection.delete(id);
    setSelectedIds(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };


  // Helper function to check if furniture intersects with selection box
  const intersectsWithSelection = (item, selRect) => {
    // Define furniture bounding box (adjust these values based on your furniture dimensions)
    const furnitureWidth = 40; // Approximate width
    const furnitureHeight = 40; // Approximate height

    const itemRect = {
      x: item.x,
      y: item.y,
      width: furnitureWidth,
      height: furnitureHeight
    };

    return !(itemRect.x + itemRect.width < selRect.x ||
             itemRect.x > selRect.x + selRect.width ||
             itemRect.y + itemRect.height < selRect.y ||
             itemRect.y > selRect.y + selRect.height);
  };

  // Function to move all selected items
  const moveSelectedItems = (dx, dy, furniture) => {
    if (selectedIds.size === 0) return furniture;

    return furniture.map(item => {
      if (selectedIds.has(item.id)) {
        return {
          ...item,
          x: item.x + dx,
          y: item.y + dy
        };
      }
      return item;
    });
  };

  return {
    selectedIds,
    selectionBox,
    startSelection,
    updateSelection,
    endSelection,
    clearSelection,
    addToSelection,
    removeFromSelection,
    setSelectedIds,
    moveSelectedItems,
    isDragging: isDraggingRef
  };
}

// Selection box visual component
export const SelectionBoxView = ({ selectionBox }) => {
  if (!selectionBox) return null;

  return (
    <rect
      x={selectionBox.x}
      y={selectionBox.y}
      width={selectionBox.width}
      height={selectionBox.height}
      fill="rgba(100, 150, 255, 0.2)"
      stroke="rgba(100, 150, 255, 0.8)"
      strokeWidth="1"
      pointerEvents="none"
    />
  );
};